// @flow
import React, { createRef, PureComponent } from "react";
import { css } from "glamor";
import getMediaQuery from "utils/sassVarsUtils";

import "./PreviewImage.scss";

type Props = {
	url: ?string,
	img: ?Object,
	crop: ?Object,
	noPreview: Function,
	dimensions?: [number, number]
};

type State = {
	loadFailed: boolean,
	loadSuccess: boolean
};

class PreviewImage extends PureComponent<Props, State> {
	initialState = {
		loadFailed: false,
		loadSuccess: false,
	};
	state = this.initialState;
	
	componentDidMount() {
		if (this.containerRef.current && this.props.img) {
			this.containerRef.current.appendChild(this.props.img);
			this.onImageLoad();
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.url !== this.props.url) {
			this.setState(this.initialState);
		}
	}

	onImageError() {
		this.setState({ loadFailed: true });
	}
	
	onImageLoad() {
		this.setState({ loadSuccess: true });
	}
	
	containerRef = createRef<Object>();
	
	render() {
		// Placeholder to display until image is loaded or in case loading failed
		const placeHolder = (!this.props.img && !this.state.loadSuccess) || this.state.loadFailed ? this.props.noPreview() : null;
		
		const { crop, dimensions } = this.props;
		const styles = css({
			height: "100%",
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			overflow: "hidden",
			"& .cropper": crop && dimensions ? {
				position: "relative",
				overflow: "hidden",
				height: 0,
				// Ratio width/height calculations
				// on the square display (>1 upload or mobile) this is framed as square.
				// in the sektop single image display, ratio factor is 56 (9/16),
				// ToDo: handle desktop multiple uploads grid (should behave like mobile).
				width: crop.width < crop.height ? `${100 * crop.width / crop.height}%` : "100%",
				paddingTop: crop.height < crop.width ? `${100 * crop.height / crop.width}%` : "100%",
				[getMediaQuery("ns")]: {
					width: crop.width <= crop.height ? `${56 * crop.width / crop.height}%` : "100%",
					paddingTop: crop.height < crop.width ? `${100 * crop.height / crop.width}%` : "56%",
				},
			} : {
				height: "100%",
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
			"& img": crop && dimensions ? {
				position: "absolute",
				objectFit: "initial",
				width: `${dimensions[0] / crop.width * 100}%`,
				height: "auto",
				maxHeight: "none",
				maxWidth: "none",
				left: 0,
				top: 0,
				transform: `translate(${-100 * crop.x / dimensions[0]}%, ${-100 * crop.y / dimensions[1]}%)`,
			} : {},
		});

		return <>
			{placeHolder}
			
			{!this.state.loadFailed
			// in case of cropped result or url upload, render image tag directly
			// otherwise render container for the img object we received from the uploader
			&& <div className={styles}
				data-test="preview-img-container">
				<div
					className="cropper"
					ref={this.props.url ? null : this.containerRef}>
					{this.props.url
					&& <img
						src={this.props.url}
						onError={() => this.onImageError()}
						onLoad={() => this.onImageLoad()}
					/>}
				</div>
			</div>}
		</>;
	}
}

export default PreviewImage;
